import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLogout } from 'react-admin';
import { usePhoneCalls } from '../../../contexts/PhoneCallsContext/PhoneCallsContext';
import { CallStatus } from '../../../contexts/PhoneCallsContext/phone-calls.types';
import { sessionMonitor } from '../../../services/session';

/** Call states in which there is nothing live to protect. */
const INACTIVE_CALL_STATUSES: CallStatus[] = [
  CallStatus.Idle,
  CallStatus.Ended,
  CallStatus.Failed,
  CallStatus.Rejected,
];

/**
 * Surfaces an expired session and performs the one and only sign-out.
 *
 * Must be mounted inside `<Admin>` so `useLogout` has react-admin's router and
 * store, and inside `PhoneCallsProvider` so it can tell whether a call is in
 * progress. `CustomLayout` satisfies both.
 *
 * The monitor guarantees this fires at most once per page life (its `dead` flag
 * is a one-way latch), so no cross-component coordination is needed here;
 * `handledSignOut` is only belt-and-braces against a double click.
 */
export const SessionExpirationGuard = () => {
  const logout = useLogout();
  const { activeCallState } = usePhoneCalls();

  const [expired, setExpired] = useState(false);
  const handledSignOut = useRef(false);
  const ranTeardowns = useRef(false);

  const callInProgress = !INACTIVE_CALL_STATUSES.includes(activeCallState.status);

  useEffect(() => sessionMonitor.onSessionDead(() => setExpired(true)), []);

  // Catches the tab that was left open overnight: re-checks on return to the
  // window rather than waiting for the next failed request.
  useEffect(() => {
    const recheck = () => sessionMonitor.verifyIfStale();
    window.addEventListener('focus', recheck);
    document.addEventListener('visibilitychange', recheck);
    return () => {
      window.removeEventListener('focus', recheck);
      document.removeEventListener('visibilitychange', recheck);
    };
  }, []);

  /**
   * Tear down as soon as it is safe to, without waiting for the user to click.
   *
   * While a call is live we deliberately hold off: the Twilio access token is a
   * separate JWT and call media does not touch our API, so the call survives a
   * dead session and destroying the device here would drop it. Once the call
   * ends this runs, so the agent stops being offered new calls even if they
   * leave the dialog sitting open.
   */
  useEffect(() => {
    if (!expired || callInProgress || ranTeardowns.current) {
      return;
    }
    ranTeardowns.current = true;
    void sessionMonitor.runTeardowns();
  }, [expired, callInProgress]);

  const signOut = useCallback(async () => {
    if (handledSignOut.current) {
      return;
    }
    handledSignOut.current = true;

    // Ordering matters: logout() clears react-admin's store and navigates to
    // /login, which unmounts PhoneCallsProvider. Tearing down first prevents a
    // registered Twilio device from surviving onto the login page.
    await sessionMonitor.runTeardowns();
    await logout({}, '/login', true);
  }, [logout]);

  if (!expired) {
    return null;
  }

  if (callInProgress) {
    return (
      <Snackbar open anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity='warning' variant='filled'>
          Your session has expired. You will be signed out when this call ends.
        </Alert>
      </Snackbar>
    );
  }

  return (
    <Dialog open disableEscapeKeyDown aria-labelledby='session-expired-title'>
      <DialogTitle id='session-expired-title'>Your session has expired</DialogTitle>
      <DialogContent>
        <DialogContentText>
          For your security you have been signed out. Sign in again to continue — you will be
          returned to the page you were on.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={signOut} variant='contained' autoFocus>
          Sign in again
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionExpirationGuard;
